<template>
  <table class="w-full text-[13px]" style="border-collapse: collapse">
    <!-- Percentage widths summing to 100, per app_ui/tables: the table always
         fills its container and every column shares growth proportionally.
         Pixel widths here would create a hard minimum and force a scrollbar. -->
    <colgroup>
      <col style="width: 3%" />
      <col style="width: 5%" />
      <col style="width: 6%" />
      <col style="width: 27%" />
      <col style="width: 13%" />
      <col style="width: 10%" />
      <col style="width: 9%" />
      <col style="width: 10%" />
      <col style="width: 9%" />
      <col style="width: 8%" />
    </colgroup>
    <thead>
      <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-[1]">
        <Hades_SortHeader
          sort-key="investigated" :active-key="sortKey" :direction="sortDir"
          th-class="py-2.5 pl-4 pr-1" @sort="$emit('sort', $event)"
        >&nbsp;</Hades_SortHeader>
        <Hades_SortHeader
          v-for="col in columns"
          :key="col.key"
          :sort-key="col.key"
          :active-key="sortKey"
          :direction="sortDir"
          :th-class="col.last ? TH_LAST : TH"
          @sort="$emit('sort', $event)"
        >{{ col.label }}</Hades_SortHeader>
      </tr>
    </thead>
    <tbody>
      <tr v-if="!tickets.length">
        <td colspan="10" class="px-4 py-6 text-gray-600">
          No requirements in this queue. Try clearing the customer or queue filter.
        </td>
      </tr>
      <tr
        v-for="ticket in tickets"
        :key="ticket.requirement"
        class="border-b border-gray-100 cursor-pointer"
        :class="ticket.requirement === selectedRequirement ? 'bg-gray-50' : 'hover:bg-gray-50/60'"
        @click="$emit('select', ticket)"
      >
        <td class="py-2.5 pl-4 pr-1 align-top">
          <span
            class="inline-block w-2 h-2 rounded-full mt-1"
            :class="ticket.investigated ? 'bg-gray-800' : 'border border-gray-100'"
            :title="ticket.investigated ? 'Investigated' : 'Not yet investigated'"
          />
        </td>
        <td class="px-2 py-2.5 align-top text-gray-600 font-mono text-xs">
          {{ ticket.requirement }}
        </td>
        <td
          class="px-2 py-2.5 align-top"
          :class="ticket.requirement_priority === 'p0'
            ? 'font-semibold text-red-dark' : 'text-gray-800'"
        >
          {{ priorityLabel(ticket.requirement_priority) }}
        </td>
        <td class="px-2 py-2.5 align-top text-gray-900">
          <div class="font-medium leading-snug">{{ ticket.requirement_title }}</div>
          <div class="text-[11px] text-gray-600 mt-0.5">
            {{ ticket.requirement_type }} · {{ ticket.requirement_status }}
          </div>
        </td>
        <td
          class="px-2 py-2.5 align-top"
          :class="ticket.investigated ? 'text-gray-800' : 'text-gray-400 italic'"
        >
          {{ ticket.investigated ? queueLabels(ticket) : 'Not investigated' }}
        </td>
        <td class="px-2 py-2.5 align-top text-gray-600">
          {{ ticket.investigated ? deliveryLabel(ticket.delivery) : '' }}
        </td>
        <td class="px-2 py-2.5 align-top text-gray-600">{{ ticket.tenant_name || '—' }}</td>
        <!-- UNOWNED IS THE INTERESTING STATE on a board about neglected work,
             so it is called out rather than left as a neutral em-dash. 81 of
             404 rows are unowned. -->
        <td class="px-2 py-2.5 align-top">
          <span v-if="ticket.owner_name" class="text-gray-800">{{ ticket.owner_name }}</span>
          <span v-else class="text-gray-400 italic">Unowned</span>
        </td>
        <td class="px-2 py-2.5 align-top text-gray-600">
          {{ ticket.creator_name || '—' }}
        </td>
        <!-- DATE PLUS AGE. The date alone does not answer "how long has this sat
             there", and an age alone loses the ability to scan for a cluster of
             filings on one day. Age is emphasised over 90 days, which is the
             threshold at which a requirement is stale rather than merely open. -->
        <td class="pl-2 pr-4 py-2.5 align-top">
          <template v-if="ticket.requirement_created_at">
            <div class="text-gray-600 whitespace-nowrap">
              {{ shortDate(ticket.requirement_created_at) }}
            </div>
            <div
              class="text-[11px] mt-0.5 whitespace-nowrap"
              :class="isStale(ticket.requirement_created_at)
                ? 'text-red-dark font-medium' : 'text-gray-400'"
            >
              {{ ageLabel(ticket.requirement_created_at) }}
            </div>
          </template>
          <span v-else class="text-gray-400">—</span>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import Hades_SortHeader from './Hades_SortHeader.vue';
import {
  DELIVERY_LABELS, PRIORITY_LABELS, QUEUE_LABELS, label,
} from './hades_constants';

// Uppercase micro-label header, per app_ui/tables. Shared so the headers
// cannot drift from each other.
const TH = 'px-2 py-2.5 text-left text-[11px] font-semibold tracking-wide '
  + 'uppercase text-gray-600';

// Column order and labels in ONE list, so the headers cannot fall out of step
// with the sort keys. `key` is what the comparator sorts on.
const COLUMNS = [
  { key: 'requirement', label: 'ID' },
  { key: 'requirement_priority', label: 'Pri' },
  { key: 'requirement_title', label: 'Requirement' },
  { key: 'queues', label: 'Queue' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'tenant_name', label: 'Tenant' },
  { key: 'owner_name', label: 'Owner' },
  { key: 'creator_name', label: 'Created by' },
  { key: 'requirement_created_at', label: 'Created', last: true },
];

export default {
  name: 'HadesTicketTable',
  components: { Hades_SortHeader },
  props: {
    tickets: { type: Array, required: true },
    // KEYED ON requirement, NOT id. An un-investigated row has id === null
    // (there is no HadesTicket yet), so every one of them collided on a null
    // key and selecting any of them highlighted all of them.
    selectedRequirement: { type: Number, default: null },
    // SORT STATE LIVES IN THE PARENT, not here. The parent owns the row array
    // and applies the comparator; this component only renders headers and emits
    // which one was clicked. Two sources of truth for "how is this sorted"
    // is how a table ends up showing one order and claiming another.
    sortKey: { type: String, default: null },
    sortDir: { type: String, default: 'asc' },
  },
  emits: ['select', 'sort'],
  data() {
    return { TH, TH_LAST: `${TH} pr-4`, columns: COLUMNS };
  },
  methods: {
    queueLabels(ticket) {
      return (ticket.queues || []).map((q) => label(QUEUE_LABELS, q)).join(', ');
    },
    deliveryLabel(delivery) {
      return label(DELIVERY_LABELS, delivery);
    },
    priorityLabel(priority) {
      return PRIORITY_LABELS[priority] || priority || '—';
    },
    // Deliberately NOT utils/date.js formatRelativeTime — date-fns returns
    // "about 5 months ago", which is three words too long for a table column
    // and wraps the cell. These rows span Mar to Aug, so months are the working
    // unit; d/mo keeps the column narrow and scannable down its length.
    // GUARD ON THE INPUT, NOT JUST ON NaN. `new Date(null)` is epoch 0 — a
    // perfectly valid date — so a NaN check alone lets a null through and
    // renders "Dec 31, 69 / 689mo" flagged stale. `new Date(undefined)` IS NaN.
    // The two behave differently and only one is caught by isNaN.
    ageDays(iso) {
      if (!iso) return null;
      const then = new Date(iso);
      if (Number.isNaN(then.getTime())) return null;
      return Math.floor((Date.now() - then.getTime()) / 86400000);
    },
    ageLabel(iso) {
      const days = this.ageDays(iso);
      if (days === null) return '';
      if (days < 1) return 'today';
      if (days < 60) return `${days}d`;
      return `${Math.floor(days / 30)}mo`;
    },
    isStale(iso) {
      const days = this.ageDays(iso);
      return days !== null && days >= 90;
    },
    shortDate(iso) {
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      // RENDERED IN UTC, matching how the timestamp is stored. Left to local
      // time, a requirement filed at 03:21 UTC on Mar 2 displays as "Mar 1" to
      // anyone west of Greenwich — the date shown would not be the date in the
      // database, and two people would disagree about when a ticket was filed.
      const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' };
      if (date.getUTCFullYear() !== new Date().getUTCFullYear()) opts.year = '2-digit';
      return date.toLocaleDateString(undefined, opts);
    },
  },
};
</script>
